import { useEffect, useState } from 'react'
import { getLive, type AdminLiveOverview } from '../lib/api'

/**
 * Was gerade im Lab passiert: wie viele Gespräche laufen, an welchem Stand,
 * was zuletzt gefragt wurde. Für Messetage und für die Frage „hängt K4ia
 * gerade?" — ohne Namen, ohne Geräte, nur Zähler und die letzte Frage. Liest
 * alle fünf Sekunden neu; die Daten kommen aus dem D1-Spiegel, der nach jedem
 * Zug geschrieben wird.
 */
const INTERVALL_MS = 5000
const AKTIV_MS = 5 * 60_000

export function LivePage() {
  const [data, setData] = useState<AdminLiveOverview | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [jetzt, setJetzt] = useState(() => Date.now())

  useEffect(() => {
    let aktiv = true
    async function laden() {
      try {
        const d = await getLive()
        if (!aktiv) return
        setData(d)
        setFehler(null)
        setJetzt(Date.now())
      } catch (err) {
        if (aktiv) setFehler(err instanceof Error ? err.message : String(err))
      }
    }
    void laden()
    const t = setInterval(() => void laden(), INTERVALL_MS)
    return () => {
      aktiv = false
      clearInterval(t)
    }
  }, [])

  return (
    <div className="max-w-4xl space-y-8">
      <div className="space-y-2">
        <h2 className="page-title">Live</h2>
        <p className="text-sm leading-relaxed text-text-muted">
          Gespräche der letzten Stunde, aktualisiert alle fünf Sekunden. Anonym: Sitzungen sind
          Zufalls-IDs, gezeigt werden nur Stand, letzte Frage und Zähler.
        </p>
      </div>

      {fehler && (
        <p role="alert" className="text-sm text-status-error">
          {fehler}
        </p>
      )}

      {data && (
        <>
          <section className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Kachel label="Gerade aktiv" value={String(data.activeNow)} hint="letzte 5 Minuten" />
            <Kachel label="Letzte Stunde" value={String(data.sessions.length)} hint="Gespräche" />
            <Kachel label="Heute" value={String(data.sessionsToday)} hint="Gespräche seit 0 Uhr" />
          </section>

          {data.sessions.length === 0 ? (
            <p className="card p-5 text-sm text-text-muted">
              Gerade spricht niemand mit K4ia. Sobald ein Gespräch beginnt, steht es hier.
            </p>
          ) : (
            <ul className="space-y-2">
              {data.sessions.map((s) => {
                const zeit = s.lastMessageAt ?? s.startedAt
                const aktiv = jetzt - new Date(zeit).getTime() < AKTIV_MS
                return (
                  <li key={s.id} className="card flex items-start gap-3 p-4">
                    <span
                      aria-label={aktiv ? 'aktiv' : 'ruhig'}
                      className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                        aktiv ? 'bg-status-active' : 'bg-border'
                      }`}
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
                        <span>{vorZeit(zeit, jetzt)}</span>
                        <span>{KANAL[s.channel] ?? s.channel}</span>
                        <span>
                          {s.turns} {s.turns === 1 ? 'Frage' : 'Fragen'}
                        </span>
                        {s.groupMode && <span>Gruppe</span>}
                      </div>
                      <p className="font-medium">{s.showcase ?? 'Noch an keinem Stand'}</p>
                      {s.lastQuestion && (
                        <p className="truncate text-sm text-text-muted">„{s.lastQuestion}“</p>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

const KANAL: Record<string, string> = {
  web: 'Web',
  voice: 'Sprache',
  teams: 'Teams',
  kiosk: 'Kiosk',
}

function Kachel({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card p-4">
      <p className="text-xs text-text-muted">{label}</p>
      <p className="text-2xl font-semibold">{value}</p>
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  )
}

export function vorZeit(iso: string, jetzt: number): string {
  const sek = Math.max(0, Math.round((jetzt - new Date(iso).getTime()) / 1000))
  if (sek < 60) return 'gerade eben'
  const min = Math.round(sek / 60)
  if (min < 60) return `vor ${min} Min.`
  return `vor ${Math.round(min / 60)} Std.`
}
